import { useState } from 'react';
import { Link } from 'react-router-dom';
import API_CONFIG from '../config/api';
import { useBranding, useDocumentBranding } from '../utils/loginBranding';
import './AdminLoginPage.css';
import './ForgotPasswordPage.css';

function ForgotPasswordPage() {
    // Browser-tab title and icon follow Settings → Login Page Branding.
    const brand = useBranding();
    useDocumentBranding(brand, `${brand.brandName} Admin Portal`);
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!email) {
            setError('Please enter your email address');
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });

            // The backend always returns a generic success message here, even if the
            // email doesn't match an account — this avoids leaking which admin
            // accounts exist to an unauthenticated caller.
            await response.json();
            setSubmitted(true);
        } catch (err) {
            setError('Server connection failed. Please check your network and try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="admin-login-page">
            <div className="admin-login-bg">
                <img src={`${process.env.PUBLIC_URL || ''}/admin-login-bg.png`} alt="" aria-hidden="true" />
            </div>

            <div className="auth-page-center">
                <div className="admin-login-form-container">
                    <div className="admin-login-card">
                        <div className="admin-login-header">
                            <h1 className="admin-page-title">Forgot Password</h1>
                            <p className="admin-page-subtitle">
                                {submitted
                                    ? 'Check your inbox for a link to reset your password.'
                                    : 'Enter the email on your admin account and we\'ll send you a reset link.'}
                            </p>
                        </div>

                        {error && (
                            <div className="admin-alert">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                </svg>
                                <span>{error}</span>
                            </div>
                        )}

                        {submitted ? (
                            <div className="auth-success-state">
                                <div className="auth-success-icon">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                    </svg>
                                </div>
                                <p>If <strong>{email}</strong> is registered, a reset link is on its way. The link expires in 30 minutes.</p>
                                <button type="button" className="auth-link-btn" onClick={() => setSubmitted(false)}>
                                    Try a different email
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="admin-form">
                                <div className="admin-input-group">
                                    <label htmlFor="email">Email</label>
                                    <div className="input-with-icon">
                                        <div className="input-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                                <polyline points="22,6 12,13 2,6"></polyline>
                                            </svg>
                                        </div>
                                        <input
                                            type="email"
                                            id="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="you@company.com"
                                            disabled={isLoading}
                                            required
                                        />
                                    </div>
                                </div>

                                <button type="submit" className="admin-submit-btn" disabled={isLoading}>
                                    {isLoading ? (
                                        <div className="btn-content">
                                            <div className="admin-spinner"></div>
                                            <span>Sending link...</span>
                                        </div>
                                    ) : (
                                        <div className="btn-content">
                                            <span>Send Reset Link</span>
                                        </div>
                                    )}
                                </button>
                            </form>
                        )}

                        <div className="auth-back-link">
                            <Link to="/admin/login">← Back to Sign In</Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ForgotPasswordPage;
